import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Truck as TruckIcon, Users, MapPin, Donut, ArrowRight } from "lucide-react";
import { ZACH_WINDOW_IMG } from "@/data/content";
import { FollowJourneyPill } from "@/components/FollowJourneyPill";
import { WatchStoryDonut } from "@/components/WatchStoryDonut";

const points = [
  { icon: TruckIcon, title: "Rolling kitchen", text: "We crunched the numbers and a donut truck just made sense — so we hit the road." },
  { icon: Donut, title: "Zach is our donut-cutter extraordinaire", text: "He's usually in the back of the truck, but he loves to pop his head out and give you his iconic smile.", hideOnMobile: true },
  { icon: Users, title: "All hands on deck", text: "All six of our other kids also pitch in. (Except our youngest, because she's only 4!) Together, they're Zach's biggest cheerleaders." },
  { icon: MapPin, title: "Made in Mattawan", text: "Proudly serving Mattawan, Paw Paw, Lawrence and beyond, week after week." },
];

export const Truck = () => {
  return (
    <section id="truck" data-testid="truck-section" className="py-14 sm:py-20 md:py-32 px-5 md:px-10 woodgrain">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#111111] tracking-tight flex items-end gap-3">
            <img
              src="/logo-truck.png?v=3"
              alt="Bobo's Donuts truck"
              className="h-[81px] sm:h-[95px] w-auto object-contain shrink-0 select-none drop-shadow-[0_6px_10px_rgba(62,39,35,0.18)]"
            />
            Meet Bobo
          </h2>
          <p className="text-lg sm:text-xl text-[#111111] mt-5 leading-relaxed font-medium">
            "Bobo" is a childhood nickname for our son Zach. Today, he's a recent
            high school graduate, but his nickname has stuck.
          </p>
          <p className="text-lg text-[#3d3d3d] mt-4 leading-relaxed">
            Our family has read the statistics — that Deaf men of color,
            particularly those with additional disabilities, are far less likely
            to find work than their hearing peers. So our family set out to build
            something of our own — a local business that could create a future for
            our son and other adults with differing abilities.
          </p>

          <div className="mt-8 space-y-5">
            {points.map((p) => (
              <div
                key={p.title}
                className={`gap-4 items-start ${p.hideOnMobile ? "hidden sm:flex" : "flex"}`}
              >
                <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#ff648b] flex items-center justify-center shadow">
                  <p.icon className="h-6 w-6 text-[#111111]" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-xl text-[#111111]">{p.title}</h3>
                  <p className="text-[#3d3d3d]">{p.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-9 flex flex-col items-start gap-4">
            <Link
              to="/about"
              data-testid="truck-learn-more"
              className="inline-flex items-center gap-2 px-7 py-3 sm:py-4 rounded-full border-2 border-transparent bg-[#ff648b] text-[#111111] font-bold text-lg hover:bg-[#ed4a76] hover:scale-105 transition-all shadow-md"
            >
              Learn More <ArrowRight className="h-5 w-5" />
            </Link>
            <FollowJourneyPill />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative hidden sm:block">
            <div className="absolute -inset-4 bg-[#ff648b]/40 rounded-[2.5rem] rotate-3" />
            <img
              src={ZACH_WINDOW_IMG}
              alt="Zach greeting friends from the Bobo's Donuts truck window with his signature smile"
              data-testid="truck-zach-photo"
              className="relative w-full h-auto rounded-3xl shadow-2xl ring-4 ring-white/60 object-cover select-none"
            />
          </div>
          <p
            data-testid="truck-zach-caption"
            className="hidden sm:block mt-8 text-center font-display font-semibold text-[#111111] text-xl"
          >
            Zach (Bobo) opening the truck for business!
          </p>

          <div className="mt-4 sm:mt-10 sm:translate-y-[20%]">
            <WatchStoryDonut caption="Press play to watch Bobo's story" sizeClass="w-[255px] sm:w-[289px]" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
